import React from 'react';
import {
  DesignThumbnailWrapper,
  LandingPageDesignCardWrapper,
  DesignTitle,
  ActiveCircle,
  ActiveText,
  InactiveCircle,
  InactiveText,
  SecondaryText,
  DesignThumbnailActionWrapper,
  DesignThumbnailOverlay,
} from '../styles';
import { Flex, Gap } from 'lib/components/styles/layout';
import {
  LP_BUILDER_FORM_MODE,
  LP_BUILDER_MODAL_STEPS,
  LpBuilderFormMode,
  TEMPLATE_ICON_MAP,
} from '../constants';
import { LandingPageDesignBadgeList } from './LandingPageDesignBadgeList';
import { IoMdEye, IoMdLink } from 'react-icons/io';
import { successToast } from 'lib/components/toasts/success';
import { LandingPageDesignActionItems } from './LandingPageDesignActionItems';
import { Button } from 'react-covideo-common';
import { useTheme } from 'styled-components/macro';
import {
  ILandingPageDesignGroup,
  ILandingPageDesignResponse,
  LP_BUILDER_MODAL_STEP,
} from '../types';
import { useIsMutating } from 'react-query';
import { landingPageDesignsKeys } from 'lib/api/superadmin/landingPageDesigns/landingPageDesignsKeys';
import { Label } from 'app/pages/design/landingPages-v1/index.styled';
import { CheckboxInput } from 'lib/components';
import { LandingPageDesignThumbnailPreview } from '../components/preview/ThumbnailPreview/LandingPageDesignThumbnailPreview';
import { MdEdit } from 'react-icons/md';
import { useCurrentAppUrl } from 'lib/hooks/useCurrentAppUrl';

export interface ILandingPageDesignCard {
  landingPageDesign: ILandingPageDesignResponse;
  setMorePopup: React.Dispatch<React.SetStateAction<string | null>>;
  morePopup: string | null;
  togglePopup: (id: string | null) => void;
  setSelectedTemplateDesignId: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  setStep: React.Dispatch<React.SetStateAction<LP_BUILDER_MODAL_STEP>>;
  batchEditTemplates: ILandingPageDesignResponse[];
  setBatchEditTemplates: React.Dispatch<
    React.SetStateAction<ILandingPageDesignResponse[]>
  >;
  setModalState: React.Dispatch<
    React.SetStateAction<{
      mode: LpBuilderFormMode;
      open: boolean;
    }>
  >;
}

export const LandingPageDesignCard = ({
  landingPageDesign,
  setMorePopup,
  morePopup,
  togglePopup,
  setSelectedTemplateDesignId,
  setStep,
  batchEditTemplates,
  setBatchEditTemplates,
  setModalState,
}: ILandingPageDesignCard) => {
  const {
    id,
    title,
    category,
    landingPageDesignAccess: accessList,
    groups,
  } = landingPageDesign;
  const theme = useTheme();
  const { landingPageUrl } = useCurrentAppUrl();

  const isDuplicating =
    useIsMutating({
      mutationKey: landingPageDesignsKeys.duplicate(),
    }) > 0;

  const constructPreviewUrl = () => {
    return `${landingPageUrl}preview/${landingPageDesign.id}?showSalesContent=true`;
  };

  const copyLink = async () => {
    const previewUrl = constructPreviewUrl();
    const cb = navigator.clipboard;
    await cb.writeText(previewUrl);
    successToast({ title: 'Link copied successfully!' });
  };

  const showLandingPagePreview = async () => {
    const previewUrl = constructPreviewUrl();
    window.open(previewUrl, '_blank');
  };

  const handleEditTemplateDesignClick = (type: LP_BUILDER_MODAL_STEP) => {
    setModalState({ mode: LP_BUILDER_FORM_MODE.EDIT, open: true });
    setSelectedTemplateDesignId(id);
    setStep(type);
  };

  const loading = isDuplicating;
  const groupTitles = groups
    .map((group: ILandingPageDesignGroup) => group.title)
    .join(', ');

  const onTemplateSelected = () => {
    const exists = batchEditTemplates.find(lpDesign => lpDesign.id === id);
    if (exists) {
      // remove
      setBatchEditTemplates(prev =>
        prev.filter(lpDesign => lpDesign.id !== id)
      );
    } else {
      // add
      setBatchEditTemplates(prev => [...prev, landingPageDesign]);
    }
  };

  return (
    <LandingPageDesignCardWrapper>
      <Gap gap='8px'>
        <CheckboxInput
          name={`${id}-checkbox`}
          checked={!!batchEditTemplates.find(lpDesign => lpDesign.id === id)}
          onChange={onTemplateSelected}
        />
        <Label style={{ marginBottom: '0' }}>Select Template</Label>
      </Gap>
      <DesignThumbnailWrapper>
        <LandingPageDesignThumbnailPreview
          landingPageDesign={landingPageDesign}
          extendStyles={{ container: { maxWidth: '280px', height: '176px' } }}
        />
        <DesignThumbnailOverlay className='overlay'>
          <DesignThumbnailActionWrapper>
            <Button
              onClick={() => showLandingPagePreview()}
              text='View'
              icon={<IoMdEye size={20} />}
              variant='secondary'
              size='small'
              disabled={loading}
            />
            <Button
              onClick={() =>
                handleEditTemplateDesignClick(LP_BUILDER_MODAL_STEPS.design)
              }
              text='Edit'
              icon={<MdEdit size={20} />}
              variant='secondary'
              size='small'
              disabled={loading}
            />
          </DesignThumbnailActionWrapper>
        </DesignThumbnailOverlay>
      </DesignThumbnailWrapper>
      <Flex gap='5px' height='47px'>
        <Flex
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
          height='24px'
        >
          {TEMPLATE_ICON_MAP[category]}
          <LandingPageDesignActionItems
            landingPageDesign={landingPageDesign}
            showLandingPagePreview={showLandingPagePreview}
            handleEditTemplateDesignClick={handleEditTemplateDesignClick}
            accessList={accessList}
          />
        </Flex>
        <SecondaryText>{groupTitles}</SecondaryText>
      </Flex>
      <DesignTitle title={title}>{title}</DesignTitle>
      <LandingPageDesignBadgeList
        accessList={accessList}
        templateId={id}
        setMorePopup={setMorePopup}
        morePopup={morePopup}
        togglePopup={togglePopup}
      />
      <Flex
        flexDirection='row'
        justifyContent='space-between'
        width='100%'
        height='24px'
      >
        {accessList.length > 0 ? (
          <Flex
            flexDirection='row'
            gap='8px'
            justifyContent='flex-start'
            alignItems='center'
          >
            <ActiveCircle />
            <ActiveText>Active</ActiveText>
          </Flex>
        ) : (
          <Flex
            flexDirection='row'
            gap='8px'
            justifyContent='flex-start'
            alignItems='center'
          >
            <InactiveCircle />
            <InactiveText>Inactive</InactiveText>
          </Flex>
        )}
        <Flex
          flexDirection='row'
          gap='8px'
          alignItems='center'
          justifyContent='flex-end'
          height='24px'
        >
          <Button
            style={{
              cursor: 'pointer',
            }}
            variant='transparent'
            size='small'
            text='Copy link'
            onClick={copyLink}
            icon={<IoMdLink />}
            iconPosition='right'
            disabled={loading}
            extendStyles={{
              container: {
                padding: 0,
                height: 24,
              },
              text: {
                fontSize: '14px',
                color: theme.colors.neutral[100],
                fontWeight: 500,
                textTransform: 'uppercase',
              },
            }}
          />
        </Flex>
      </Flex>
    </LandingPageDesignCardWrapper>
  );
};
